import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import HowToUseSection from '@/components/HowToUseSection';
import { HELP_URLS } from '@/components/HelpBenefits';
import WhatsNewSection from '@/components/WhatsNewSection';
import { ArrowLeft, Thermometer, ChevronDown, ChevronUp, TrendingUp, Target, BarChart3, CheckCircle, BookOpen, Lock, Upload, Clock, Settings, MousePointer, MapPin, Calendar, Waves } from 'lucide-react';

export default function TemperatureModelBenefits() {
  const navigate = useNavigate();
  const [isBenefitsOpen, setIsBenefitsOpen] = useState(true);
  const [isProcessOpen, setIsProcessOpen] = useState(true);

  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 text-[#0891b2] hover:text-[#0e7490] hover:bg-[#0891b2]/10"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Main Content Card */}
        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-[#0891b2] to-[#0e7490] text-white rounded-t-lg px-4 py-3 sm:px-6 sm:py-4">
            <CardTitle className="text-lg sm:text-2xl flex items-center gap-2 sm:gap-3">
              <Thermometer className="h-5 w-5 sm:h-7 sm:w-7" />
              Custom Temperature Profile Upload
            </CardTitle>
          </CardHeader>
          
          <CardContent className="p-4 sm:p-6 space-y-4">
            
            {/* Upload Process Section - Collapsible */}
            <Collapsible open={isProcessOpen} onOpenChange={setIsProcessOpen}>
              <CollapsibleTrigger asChild>
                <button 
                  className="w-full flex items-center justify-between p-3 sm:p-4 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                  data-testid="process-toggle"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-2 bg-purple-100 rounded-full">
                      <Settings className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                    </div>
                    <span className="font-semibold text-purple-700 text-sm sm:text-base">Upload Process & How to Use Your Temperature Profile</span>
                  </div>
                  {isProcessOpen ? (
                    <ChevronUp className="h-5 w-5 text-purple-600" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-purple-600" />
                  )}
                </button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-3 space-y-4">
                {/* Step 1: Received */}
                <section className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      1
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Upload className="h-4 w-4 text-blue-600" />
                        <h4 className="text-sm sm:text-base font-semibold text-blue-800">Received</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        Once you upload your Excel file containing your site-specific temperature data, the system immediately marks it as <strong>"Received"</strong>. You'll see this status in your Temperature Profile Manager. This confirms your file has been successfully uploaded and is queued for review.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Step 2: Processing */}
                <section className="bg-amber-50 border border-amber-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-amber-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      2
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Clock className="h-4 w-4 text-amber-600" />
                        <h4 className="text-sm sm:text-base font-semibold text-amber-800">Processing</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        The TPS team reviews your temperature profile for data completeness and validates the format. During this phase, the status changes to <strong>"Processing"</strong>. This ensures your temperature data meets the technical requirements (date ranges, temperature values, seasonal coverage) and will integrate correctly with the growth models.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Step 3: Approved & Installed */}
                <section className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      3
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <h4 className="text-sm sm:text-base font-semibold text-green-800">Approved & Installed</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        Once verified, your temperature profile is marked as <strong>"Approved & Installed"</strong>. It is now active in your account and ready to use. You'll receive confirmation and can immediately begin using your site-specific temperature data in analyses.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Step 4: How to Select Your Temperature Profile */}
                <section className="bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-[#0891b2] text-white rounded-full flex items-center justify-center font-bold text-sm">
                      4
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <MousePointer className="h-4 w-4 text-[#0891b2]" />
                        <h4 className="text-sm sm:text-base font-semibold text-[#0891b2]">How to Select Your Custom Temperature Profile</h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-3">
                        After approval, your custom temperature profile becomes available in the following analysis modules:
                      </p>
                      <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <TrendingUp className="h-4 w-4 text-[#0891b2] mt-0.5 flex-shrink-0" />
                          <span><strong>Growth vs Model Analysis:</strong> Select "My Temperature Profiles" from the Temperature Source dropdown to use your site-specific temperature data for growth modeling.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <BarChart3 className="h-4 w-4 text-[#0891b2] mt-0.5 flex-shrink-0" />
                          <span><strong>Growth Prognosis:</strong> Choose your custom temperature profile to forecast future growth using your actual site temperature patterns.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Target className="h-4 w-4 text-[#0891b2] mt-0.5 flex-shrink-0" />
                          <span><strong>Spreadsheet Growth Comparison:</strong> Apply your site-specific temperature data when analyzing uploaded spreadsheet data for more accurate model comparisons.</span>
                        </li>
                      </ul>
                      <div className="mt-3 p-3 bg-white rounded-lg border border-[#0891b2]/30">
                        <p className="text-xs sm:text-sm text-[#0891b2] font-medium">
                          Tip: Look for the "Temperature Source" dropdown in each analysis tool. Select "My Temperature Profiles" to see your custom profiles, or use the default regional profiles.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>
              </CollapsibleContent>
            </Collapsible>
            
            <WhatsNewSection />

            {/* Learn the Benefits Section - Collapsible */}
            <Collapsible open={isBenefitsOpen} onOpenChange={setIsBenefitsOpen}>
              <CollapsibleTrigger asChild>
                <button 
                  className="w-full flex items-center justify-between p-3 sm:p-4 bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg hover:bg-[#0891b2]/10 transition-colors"
                  data-testid="benefits-toggle"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-2 bg-[#0891b2]/10 rounded-full">
                      <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-[#0891b2]" />
                    </div>
                    <span className="font-semibold text-[#0891b2] text-sm sm:text-base">Learn the Benefits of Custom Temperature Profile Upload</span>
                  </div>
                  {isBenefitsOpen ? (
                    <ChevronUp className="h-5 w-5 text-[#0891b2]" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-[#0891b2]" />
                  )}
                </button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="mt-3 space-y-4">
                {/* Introduction */}
                <section className="bg-slate-50 border border-slate-200 rounded-lg p-4 sm:p-5">
                  <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-2 sm:mb-3">
                    Why Upload Your Own Temperature Profile?
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                    Temperature is the single most important environmental factor affecting fish growth. Every aquaculture site experiences unique temperature patterns based on location, depth, season, and local oceanographic conditions. Using accurate, site-specific temperature data dramatically improves the precision of growth modeling and forecasting.
                  </p>
                </section>

                {/* Why Temperature Matters */}
                <section className="bg-amber-50 border border-amber-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-amber-800 mb-3 flex items-center gap-2">
                    <Thermometer className="h-4 w-4" />
                    Why Temperature Data Matters
                  </h4>
                  <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <Waves className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span><strong>SGR is temperature-dependent:</strong> Fish growth rate (SGR) varies significantly with water temperature. A 2°C difference can change daily growth by 10-15%.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Calendar className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Seasonal patterns vary by site:</strong> Generic regional profiles may not capture your specific site's temperature curve, especially in areas with unique thermal characteristics.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <span><strong>Microclimate effects:</strong> Depth, current patterns, and proximity to thermal sources create site-specific temperature profiles that generic models cannot capture.</span>
                    </li>
                  </ul>
                </section>

                {/* Feature List */}
                <section className="bg-blue-50 border border-blue-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-blue-800 mb-3">The Custom Temperature Upload feature allows you to:</h4>
                  <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <Thermometer className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Use your own recorded temperature data as the basis for growth modeling</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Create site-specific temperature profiles for each farm location</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Calendar className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Capture your actual seasonal temperature patterns (daily, weekly, or monthly averages)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <BarChart3 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Replace generic regional assumptions with validated site data</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Target className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <span>Improve accuracy of harvest timing predictions by 2-4 weeks</span>
                    </li>
                  </ul>
                </section>

                {/* Precision Benefits */}
                <section className="bg-green-50 border border-green-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-green-800 mb-3">This increases precision in:</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                      <TrendingUp className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Growth trajectory modeling</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                      <Target className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Harvest date forecasting</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                      <BarChart3 className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Feed requirement planning</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                      <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Model vs actual comparisons</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                      <Thermometer className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Seasonal growth projections</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-700">
                      <Calendar className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <span>Multi-cycle planning</span>
                    </div>
                  </div>
                </section>

                {/* Data Format Info */}
                <section className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 sm:p-5">
                  <h4 className="text-xs sm:text-sm font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                    <Upload className="h-4 w-4" />
                    What Data to Include
                  </h4>
                  <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mb-3">
                    Your temperature profile should include:
                  </p>
                  <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 font-bold">•</span>
                      <span><strong>Date or Day of Year:</strong> Daily, weekly, or monthly temperature readings covering a full year (or multiple years for averaging)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 font-bold">•</span>
                      <span><strong>Temperature values:</strong> Average water temperature in °C at cage depth</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 font-bold">•</span>
                      <span><strong>Site identifier:</strong> Name or code for the specific farm location</span>
                    </li>
                  </ul>
                  <div className="mt-3 p-3 bg-white rounded-lg border border-indigo-200">
                    <p className="text-xs sm:text-sm text-indigo-700">
                      Tip: Historical data from multiple years can be averaged to create a "typical year" profile that smooths out anomalies.
                    </p>
                  </div>
                </section>

                {/* Privacy Note */}
                <section className="bg-purple-50 border border-purple-200 rounded-lg p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 rounded-full flex-shrink-0">
                      <Lock className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-purple-800 mb-1">Privacy & Review</h4>
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                        Your temperature profiles remain <strong>private</strong> and are reviewed by TPS before activation to ensure data completeness and format accuracy.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Conclusion */}
                <section className="bg-[#0891b2]/5 border border-[#0891b2]/20 rounded-lg p-4 sm:p-5">
                  <p className="text-xs sm:text-sm text-[#0891b2] font-medium leading-relaxed">
                    By uploading your site-specific temperature data, you transform generic growth models into precision tools calibrated to your exact farming conditions—improving forecast accuracy and enabling better production planning.
                  </p>
                </section>
              </CollapsibleContent>
            </Collapsible>

            {/* How to Use Section - Standardized Component */}
            <HowToUseSection 
              featureName="Temperature Profile Upload"
              youtubeUrl={HELP_URLS.temperatureModelUpload.youtube}
            />

            {/* CTA */}
            <div className="pt-4 border-t border-slate-200 flex justify-center">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-[#0891b2] hover:bg-[#0e7490] text-white px-6 sm:px-8 py-2 sm:py-3 text-sm sm:text-base"
                data-testid="go-to-dashboard-btn"
              >
                <Thermometer className="h-4 w-4 mr-2" />
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
